/**
 * 商家端 Outbound SDK：以商家客服身份开会话并打开工作台。
 * 与访客 `open()` 严格拆开；path / 字段映射见 OUTBOUND_OPEN_PATH + adaptOutboundResponse。
 * 契约：docs/ADR_OUTBOUND_AGENT_SESSION.md（ADR-009）。
 */
import { composeAgentUrl, requireStringId } from './core/compose-agent-url';
import { emit } from './events';
import { getConfig } from './open';
import type { OutboundOpenOptions, OutboundSessionResult } from './outbound-types';
import type { DajiCSBootOptions } from './types';
import { HttpError, fetchWithRetry } from './utils/fetch-with-retry';
import { prepareSdkRequestHeaders } from './utils/headers';

/** 商家 outbound 开会话 path（交接 openSession；gateway 前缀与访客 open 一致） */
export const OUTBOUND_OPEN_PATH = '/api/customer/front/customer-service/openSession';

const AGENT_WINDOW_NAME = 'DJ_Agent_Workbench';

function ensureOutboundBooted(): DajiCSBootOptions {
  const cfg = getConfig();
  if (!cfg) {
    throw new Error('DajiCS.outbound: call boot() before createAgentSession');
  }
  return cfg;
}

/**
 * 将后端回包归一为 OutboundSessionResult。
 * `sessionId` / `token` 经 requireStringId（含 `0` → `'0'`）；缺字段抛 `DajiCS.outbound: missing ...`。
 */
export function adaptOutboundResponse(data: unknown): OutboundSessionResult {
  if (data === null || data === undefined || typeof data !== 'object') {
    throw new Error('DajiCS.outbound: invalid response');
  }
  const src = data as Record<string, unknown>;
  const sessionId = requireStringId(src.sessionId, 'sessionId');
  const token = requireStringId(src.token, 'token');

  const result: OutboundSessionResult = {
    sessionId,
    token,
    raw: data,
  };

  if (src.tokenExpireAt !== undefined && src.tokenExpireAt !== null) {
    result.tokenExpireAt = src.tokenExpireAt as number | string;
  }
  if (src.ryToken !== undefined && src.ryToken !== null && src.ryToken !== '') {
    result.ryToken = String(src.ryToken);
  }
  if (src.customerType !== undefined && src.customerType !== null && src.customerType !== '') {
    const n = Number(src.customerType);
    if (Number.isFinite(n)) {
      result.customerType = n;
    }
  }

  return result;
}

/**
 * 调商家 outbound 接口拿 sessionId + 工作台免登 token（不导航）。
 * HTTP body：`userId`←SDK `buyerUserId`；`customerType` 必填 ∈ {1,2}；有 `orderId` 则带上。
 */
export async function createAgentSession(opts: OutboundOpenOptions): Promise<OutboundSessionResult> {
  const cfg = ensureOutboundBooted();
  const buyerUserId = requireStringId(opts.buyerUserId, 'buyerUserId');
  const accessToken = requireStringId(opts.accessToken, 'accessToken');
  if (opts.customerType !== 1 && opts.customerType !== 2) {
    throw new Error('DajiCS.outbound: missing or invalid customerType (expected 1 | 2)');
  }
  const customerType = opts.customerType;

  let headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(cfg.headers ?? {}),
    ...(opts.headers ?? {}),
  };
  // 宿主凭证双写（与访客 openWithSession / 现网 axios 对齐；交接 curl 仅 accesstoken）
  headers.accesstoken = String(accessToken);
  headers.authorization = String(accessToken);
  if (cfg.tenant) headers['x-tenant-code'] = String(cfg.tenant);
  headers['x-auth-source'] = cfg.source || 'daji';
  headers = prepareSdkRequestHeaders(headers);

  // 线协议字段名 userId；对外 SDK 仍暴露 buyerUserId
  const body: Record<string, unknown> = { userId: buyerUserId, customerType };
  if (opts.orderId !== undefined && opts.orderId !== null && opts.orderId !== '') {
    body.orderId = String(opts.orderId);
  }

  let res: Response;
  try {
    res = await fetchWithRetry(`${cfg.apiBase}${OUTBOUND_OPEN_PATH}`, {
      method: 'POST',
      headers,
      body: JSON.stringify(body),
      credentials: 'include',
      debug: cfg.debug,
    });
  } catch (e) {
    if (e instanceof HttpError) {
      let detail = `HTTP ${e.status} ${e.statusText}`;
      try {
        const errJson: unknown = await e.response.json();
        if (errJson !== null && typeof errJson === 'object') {
          const envelope = errJson as Record<string, unknown>;
          const bizMsg =
            (typeof envelope.msg === 'string' && envelope.msg) ||
            (typeof envelope.message === 'string' && envelope.message) ||
            '';
          if (bizMsg) detail = bizMsg;
        }
      } catch {
        // ignore body parse failures; keep HTTP status text
      }
      throw new Error(`DajiCS.outbound: ${detail}`);
    }
    const msg = e instanceof Error ? e.message : String(e);
    throw new Error(`DajiCS.outbound: ${msg}`);
  }

  let json: unknown;
  try {
    json = await res.json();
  } catch {
    throw new Error('DajiCS.outbound: invalid JSON response');
  }

  if (json !== null && typeof json === 'object') {
    const envelope = json as Record<string, unknown>;
    if (envelope.code !== undefined && envelope.code !== 200) {
      const msg =
        (typeof envelope.msg === 'string' && envelope.msg) ||
        (typeof envelope.message === 'string' && envelope.message) ||
        `code=${String(envelope.code)}`;
      throw new Error(`DajiCS.outbound: ${msg}`);
    }
    const payload = envelope.data !== undefined ? envelope.data : envelope;
    return adaptOutboundResponse(payload);
  }

  return adaptOutboundResponse(json);
}

/** createAgentSession + composeAgentUrl（不导航） */
export async function buildAgentSessionUrl(
  opts: OutboundOpenOptions,
): Promise<OutboundSessionResult & { url: string }> {
  const cfg = ensureOutboundBooted();
  const session = await createAgentSession(opts);
  const url = composeAgentUrl(cfg.baseUrl, {
    token: session.token,
    sessionId: session.sessionId,
    mobile: opts.mobile,
  });
  return { ...session, url };
}

/**
 * build 后按 `navigate`（默认 `tab`）打开工作台。
 * - `tab`：手势内预开 `about:blank`（窗口名 `DJ_Agent_Workbench`），await 后再赋 `location.href`
 * - `self`：`location.href`
 * - `none`：只返回；派发 `outbound:url:ready`
 * 弹窗被拦或其它失败：派发 `error`（source=`outbound.openAgentSession`）并抛出。
 */
export async function openAgentSession(opts: OutboundOpenOptions): Promise<OutboundSessionResult & { url: string }> {
  const navigate = opts.navigate ?? 'tab';

  // 阶段 1（同步）：仍在用户手势栈内时预开空白窗，避免 await 后 window.open 被拦
  let blankWindow: Window | null = null;
  if (navigate === 'tab' && typeof window !== 'undefined') {
    blankWindow = window.open('about:blank', AGENT_WINDOW_NAME);
  }

  try {
    const result = await buildAgentSessionUrl(opts);

    if (navigate === 'none') {
      emit('outbound:url:ready', { url: result.url, sessionId: result.sessionId });
      return result;
    }

    if (typeof window === 'undefined') {
      return result;
    }

    if (navigate === 'self') {
      window.location.href = result.url;
      return result;
    }

    // navigate === 'tab'
    if (blankWindow && !blankWindow.closed) {
      blankWindow.location.href = result.url;
      return result;
    }

    throw new Error('DajiCS.outbound: window.open returned null (popup blocked?)');
  } catch (e) {
    if (blankWindow && !blankWindow.closed) {
      try {
        blankWindow.close();
      } catch {
        // ignore close failures on about:blank
      }
    }
    emit('error', { source: 'outbound.openAgentSession', error: e });
    throw e;
  }
}
